import { useEffect, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix for default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Custom marker icon creator
const createMarkerIcon = (index, completed, active, isStart = false, isDriver = false) => {
  let bgColor;
  let content;
  
  if (isDriver) {
    bgColor = "#3b82f6"; // Blue for driver
    content = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="M2 12h2"/><path d="M20 12h2"/></svg>`;
    return L.divIcon({
      className: "driver-marker",
      html: `
        <div style="
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          background: ${bgColor};
          border: 3px solid white;
          border-radius: 50%;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          animation: pulse 2s infinite;
        ">
          ${content}
        </div>
      `,
      iconSize: [36, 36],
      iconAnchor: [18, 18],
    });
  }
  
  if (isStart) {
    bgColor = "#16a34a"; // Green for start
    content = "S";
  } else {
    bgColor = completed ? "#71717A" : active ? "#FF5F00" : "#002FA7";
    content = index + 1;
  }
  
  return L.divIcon({
    className: "custom-div-marker",
    html: `
      <div style="
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        background: ${bgColor};
        color: white;
        font-family: 'Chivo', sans-serif;
        font-weight: 700;
        font-size: 14px;
        clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
        ${active && !isStart ? 'animation: pulse 1.5s infinite;' : ''}
      ">
        ${content}
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

// Decode polyline from OSRM
function decodePolyline(encoded) {
  const points = [];
  let index = 0;
  let lat = 0;
  let lng = 0;

  while (index < encoded.length) {
    let b;
    let shift = 0;
    let result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlat = result & 1 ? ~(result >> 1) : result >> 1;
    lat += dlat;

    shift = 0;
    result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlng = result & 1 ? ~(result >> 1) : result >> 1;
    lng += dlng;

    points.push([lat / 1e5, lng / 1e5]);
  }

  return points;
}

// Component to handle map bounds
function MapBounds({ destinations, route, startLocation }) {
  const map = useMap();

  useEffect(() => {
    const allPoints = startLocation 
      ? [startLocation, ...destinations]
      : destinations;
    
    if (allPoints.length === 0) {
      // Default view (UK centered)
      map.setView([54.5, -3], 6);
      return;
    }

    if (allPoints.length === 1) {
      map.setView([allPoints[0].lat, allPoints[0].lng], 14);
      return;
    }

    // Fit bounds to all points
    const bounds = L.latLngBounds(
      allPoints.map((d) => [d.lat, d.lng])
    );
    map.fitBounds(bounds, { padding: [50, 50] });
  }, [destinations, route, map, startLocation]);

  return null;
}

export default function MapComponent({ destinations, route, activeDestination, onDestinationClick, startLocation, driverLocation }) {
  const mapRef = useRef(null);

  // Decode polyline if route exists
  const routePositions = useMemo(() => {
    if (route?.polyline) {
      return decodePolyline(route.polyline);
    }
    return null;
  }, [route]);

  return (
    <MapContainer
      ref={mapRef}
      center={[54.5, -3]}
      zoom={6}
      style={{ height: "100%", width: "100%" }}
      zoomControl={true}
    >
      {/* CartoDB Voyager tiles - clean style */}
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
        url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
      />

      <MapBounds destinations={destinations} route={route} startLocation={startLocation} />

      {/* Route polyline */}
      {routePositions && (
        <Polyline
          positions={routePositions}
          pathOptions={{
            color: "#002FA7",
            weight: 4,
            opacity: 0.8,
          }}
        />
      )}

      {/* Start location marker */}
      {startLocation && (
        <Marker
          position={[startLocation.lat, startLocation.lng]}
          icon={createMarkerIcon(0, false, false, true)}
        >
          <Popup>
            <div style={{ minWidth: 150 }}>
              <div style={{ fontWeight: 700, marginBottom: 4, color: "#16a34a" }}>
                Start Location
              </div>
              <div style={{ fontFamily: "JetBrains Mono, monospace" }}>
                {startLocation.postcode}
              </div>
            </div>
          </Popup>
        </Marker>
      )}

      {/* Driver location marker */}
      {driverLocation && (
        <Marker
          position={[driverLocation.lat, driverLocation.lng]}
          icon={createMarkerIcon(0, false, false, false, true)}
        >
          <Popup>
            <div style={{ minWidth: 100 }}>
              <div style={{ fontWeight: 700, color: "#3b82f6" }}>
                Driver Location
              </div>
            </div>
          </Popup>
        </Marker>
      )}

      {/* Destination markers */}
      {destinations.map((dest, index) => (
        <Marker
          key={dest.id}
          position={[dest.lat, dest.lng]}
          icon={createMarkerIcon(index, dest.completed, activeDestination === index, false)}
          eventHandlers={{
            click: () => onDestinationClick?.(index),
          }}
        >
          <Popup>
            <div style={{ minWidth: 150 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                Stop #{index + 1}
              </div>
              {dest.name && (
                <div style={{ marginBottom: 4 }}>
                  {dest.name}
                </div>
              )}
              <div style={{ fontFamily: "JetBrains Mono, monospace" }}>
                {dest.postcode}
              </div>
              {dest.completed && (
                <div style={{ color: "#22c55e", marginTop: 8, fontSize: 11 }}>
                  Completed
                </div>
              )}
            </div>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
}
